import Button from '@/ui/Button';
import Input from '@/ui/Input';
import LoadingDots from '@/ui/LoadingDots';
import { XCircle, Send, ShieldCheck } from 'lucide-react';
import { useCallback } from 'react';

interface ValidationState {
  value: string;
  error: boolean;
  validated: boolean;
  errorMessage?: string;
}

const FORM_ERRORS = {
  PHONE_INVALID: 'Please enter a valid mobile number',
  FEEDBACK_REQUIRED: 'Please enter a valid issue description',
} as const;

interface PhoneFormProps {
  phone: ValidationState;
  isSubmitting: boolean;
  onPhoneChange: (value: string) => void;
  onSubmit: () => void;
  isValidating: boolean;
}

export const PhoneForm: React.FC<PhoneFormProps> = ({
  phone,
  isSubmitting,
  onPhoneChange,
  onSubmit,
  isValidating,
}) => {
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onPhoneChange(e.target.value);
    },
    [onPhoneChange]
  );

  const handleClear = useCallback(() => {
    onPhoneChange('');
  }, [onPhoneChange]);

  const getBorderClasses = useCallback(() => {
    if (phone.error) return '!border-red-500';
    if (phone.validated) return '!border-green-500';
    if (isValidating) return '!border-yellow-500';
    return '';
  }, [phone.error, phone.validated, isValidating]);

  return (
    <div className="space-y-4">
      <p className="font-semibold text-gray-900">Please enter your new mobile number below:</p>

      <div className="space-y-2">
        <div className="flex gap-1 sm:gap-2 items-start max-w-full">
          <div className="flex-shrink-0">
            <Input
              type="text"
              value="GB(+44)"
              readOnly
              className="!w-25 !border !border-green-500 !bg-gray-50 !text-gray-700 !font-medium !cursor-default !text-center !text-body !py-3 hidden sm:block"
            />
            <Input
              type="text"
              value="+44"
              readOnly
              className="!w-[50px] !px-1 !border !border-green-500 !bg-gray-50 !text-gray-700 !font-medium !cursor-default !text-center !text-body !py-3 block sm:hidden"
            />
          </div>

          <div className="flex-1 min-w-0">
            <Input
              id="phone-input"
              autoFocus
              type="tel"
              placeholder="Your mobile number"
              value={phone.value}
              maxLength={11}
              onChange={handleInputChange}
              error={phone.error}
              onlyNumeric
              errorMessage={phone.error ? phone.errorMessage || FORM_ERRORS.PHONE_INVALID : ''}
              className={`w-full !py-3 !text-body ${getBorderClasses()}`}
              icon={
                isValidating ? (
                  <LoadingDots size="sm" />
                ) : phone.error ? (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    aria-label="Clear phone number"
                  >
                    <XCircle size={20} className="text-red-500" />
                  </button>
                ) : (
                  <ShieldCheck size={20} className="text-green-500" />
                )
              }
              disabled={isSubmitting}
              aria-describedby={phone.error ? 'phone-error' : undefined}
            />
          </div>
        </div>
      </div>

      <Button
        onClick={onSubmit}
        block
        className="py-3"
        icon={isSubmitting ? <LoadingDots size="sm" /> : <Send />}
        aria-label={isSubmitting ? 'Submitting phone number' : 'Submit phone number'}
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </div>
  );
};

interface FeedbackFormProps {
  feedback: ValidationState;
  isSubmitting: boolean;
  onFeedbackChange: (value: string) => void;
  onSubmit: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  feedback,
  isSubmitting,
  onFeedbackChange,
  onSubmit,
}) => {
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFeedbackChange(e.target.value);
    },
    [onFeedbackChange]
  );

  const isFormValid = feedback.value.trim() && !feedback.error;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-subtitle font-semibold text-gray-900 mb-3">Having Issues?</h3>
        <p className="text-gray-700 mb-3 leading-relaxed">
          We hope you're not experiencing any technical issues on our site, but on the off-chance
          you are, please use this form to report the problem.
        </p>
        <p className="text-gray-700 mb-4 leading-relaxed">
          <span className="font-semibold">Important:</span> This form is{' '}
          <span className="font-semibold">not monitored by our customer service team.</span>
          If you have any questions regarding our services, or you would like an update on your
          claim, please contact our customer service team by emailing us at{' '}
          <a
            className="font-semibold text-green-700 hover:text-green-800 underline"
            href="mailto:claims@fair.inc"
          >
            claims@fair.inc
          </a>
        </p>
      </div>

      <Input
        placeholder="Describe your issue..."
        className="w-full min-h-24"
        rows={4}
        onChange={handleInputChange}
        error={feedback.error}
        errorMessage={feedback.error ? FORM_ERRORS.FEEDBACK_REQUIRED : ''}
        autoFocus
        value={feedback.value}
        disabled={isSubmitting}
        icon={<ShieldCheck size={24} className="text-green-500" />}
        aria-describedby={feedback.error ? 'feedback-error' : undefined}
      />

      <Button
        onClick={onSubmit}
        disabled={isSubmitting || !isFormValid}
        block
        className="py-3"
        icon={isSubmitting ? <LoadingDots size="sm" /> : <Send />}
        aria-label={isSubmitting ? 'Submitting feedback' : 'Submit feedback'}
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </div>
  );
};
